import { z } from "zod";
import { ThreadAuthor } from "./author";
import { Document } from "./document";
import { FileId } from "./file";
import { IsoDateString } from "./isoDate";
import { TipTapDocSchema } from "./tiptapDoc";
import { User } from "./user";

export const DiscussionParticipant = z.object({
    id: z.number(),
    name: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    avatarUrl: z.string().nullable(),
    isPrivateEnabled: z.boolean(),
});

export const ParticipantEvent = z.object({
    user: DiscussionParticipant,
    addedAt: IsoDateString.nullable(),
    addedBy: DiscussionParticipant.nullable(),
    removedAt: IsoDateString.nullable(),
    removedBy: DiscussionParticipant.nullable(),
});

export const Discussion = z.object({
    id: z.number(),
    title: z.string(),
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
    contentJson: TipTapDocSchema,
    authorId: z.number().nullable(),
    discussionId: z.number(),
    attachments: z.array(FileId).optional(),
});

export const DiscussionComment = z.object({
    id: z.number(),
    discussionId: z.number().optional(),
    attachments: z.array(Document),
    contentJson: TipTapDocSchema,
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
    isEdited: z.boolean().optional(),
    author: User,
});

export const DiscussionListEntry = z.object({
    id: z.number(),
    title: z.string(),
    attachmentsCount: z.number(),
    commentsCount: z.number(),
    organizations: z.array(
        z.object({
            id: z.union([z.string(), z.number()]),
            name: z.string(),
            users: z.array(DiscussionParticipant),
        }),
    ),
    // Undefined when the discussion's thread has no live comments (e.g. every
    // comment has been soft-deleted, or the discussion was just created). Fixes
    // PORTAL-API-41 — `DiscussionService.formatDiscussionListEntry` already
    // emits `lastComment: undefined` in that case, but the schema rejected it
    // and the whole `discussions.list` response failed to parse.
    lastComment: z
        .object({
            authorId: z.number().nullable(),
            author: ThreadAuthor,
            createdAt: IsoDateString,
            isRead: z.boolean(),
            isAuthorPrivateEnabled: z.boolean().optional(),
        })
        .optional(),
    authorId: z.number().nullable(),
    users: z.array(DiscussionParticipant),
    userCanEdit: z.boolean(),
    isEdited: z.boolean().optional(),
    isTitleEdited: z.boolean().optional(),
});
